import { useEffect, useMemo, useRef, useState } from 'react';
import { SpriteLevel } from '@/services/engine';
import { useSpritePreview } from '@/hooks/useSpritePreview';

const FRAME_COUNT = 10;
const FRAME_INTERVAL_MILLISECONDS = 700;

interface VideoHoverPreviewProps {
    sourceId: string;
    duration: number;
    isHovered: boolean;
}

export function VideoHoverPreview({
    sourceId,
    duration,
    isHovered,
}: VideoHoverPreviewProps) {
    const containerRef = useRef<HTMLDivElement>(null);
    const [hasBeenHovered, setHasBeenHovered] = useState(false);
    const [frame, setFrame] = useState(0);
    const [containerSize, setContainerSize] = useState({ width: 0, height: 0 });

    const { isReady, getSprite } = useSpritePreview({
        sourceId: hasBeenHovered ? sourceId : undefined,
        level: SpriteLevel.HD,
    });

    useEffect(() => {
        if (isHovered) {
            setHasBeenHovered(true);
        } else if (!isReady) {
            setHasBeenHovered(false);
        }
    }, [isHovered, isReady]);

    useEffect(() => {
        if (!isHovered || !isReady) {
            setFrame(0);
            return;
        }

        setFrame(1);
        const interval = setInterval(
            () => setFrame((current) => (current % (FRAME_COUNT - 1)) + 1),
            FRAME_INTERVAL_MILLISECONDS,
        );

        return () => clearInterval(interval);
    }, [isHovered, isReady]);

    useEffect(() => {
        if (!isHovered || !containerRef.current) return;

        const { width, height } = containerRef.current.getBoundingClientRect();
        setContainerSize({ width, height });
    }, [isHovered]);

    const sprite = useMemo(() => {
        if (!isHovered || !isReady || duration <= 0) return null;

        return getSprite((frame / FRAME_COUNT) * duration);
    }, [isHovered, isReady, getSprite, frame, duration]);

    const scale = sprite
        ? Math.max(
              containerSize.width / sprite.width,
              containerSize.height / sprite.height,
          )
        : 0;

    return (
        <div
            ref={containerRef}
            className="pointer-events-none absolute inset-0 overflow-hidden"
        >
            {sprite && scale > 0 && (
                <div
                    className="absolute left-1/2 top-1/2"
                    style={{
                        width: `${sprite.width}px`,
                        height: `${sprite.height}px`,
                        transform: `translate(-50%, -50%) scale(${scale})`,
                        backgroundImage: `url(${sprite.url})`,
                        backgroundPosition: `-${sprite.x}px -${sprite.y}px`,
                        backgroundSize: 'auto',
                        backgroundRepeat: 'no-repeat',
                    }}
                />
            )}
        </div>
    );
}
